import { FC, useState } from 'react';
import { Button, Card, Col, Input, Row, Select, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import {
	getEmailLogs,
	EmailLogsRequest,
} from '@/libs/fleet-api/services/admin';
import { SearchOutlined } from '@ant-design/icons';
import FleetTitle from '@/libs/antd/components/Title';

const { Search } = Input;
const { Option } = Select;

const AdminEmailLogsPage: FC = () => {
	const [searchParams, setSearchParams] = useState<EmailLogsRequest>({
		pagination: {
			pageSize: 50,
			pageNumber: 1,
		},
	});

	const { data: emailLogsData, isLoading } = useQuery({
		queryKey: ['admin-email-logs', searchParams],
		queryFn: () => getEmailLogs(searchParams),
	});

	const filteredLogs = emailLogsData?.data?.items ?? [];
	const totalCount = emailLogsData?.data?.totalCount ?? 0;

	const handleTableChange = (pageNumber: number, pageSize: number) => {
		setSearchParams((current) => ({
			...current,
			pagination: {
				pageSize,
				pageNumber: current.pagination?.pageSize !== pageSize ? 1 : pageNumber,
			},
		}));
	};

	const handleSearch = (
		value: string,
		searchBy: 'subject' | 'status' | 'tos',
	) => {
		setSearchParams((current) => ({
			...current,
			search: value ? { by: searchBy, value } : undefined,
			pagination: {
				...current.pagination,
				pageNumber: 1, // Reset to first page when searching
			},
		}));
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleString('id-ID', {
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit',
		});
	};

	const getStatusColor = (status: string | null) => {
		switch (status?.toLowerCase()) {
			case 'sent':
				return 'green';
			case 'failed':
				return 'red';
			case 'pending':
				return 'orange';
			default:
				return 'default';
		}
	};

	const columns = [
		{
			title: 'ID',
			dataIndex: 'id',
			key: 'id',
			width: 80,
		},
		{
			title: 'Penerima',
			dataIndex: 'tos',
			key: 'tos',
			render: (tos: string | null) => (
				<div style={{ maxWidth: '200px' }}>
					{tos ? (
						<span title={tos}>
							{tos.length > 30 ? `${tos.substring(0, 30)}...` : tos}
						</span>
					) : (
						'-'
					)}
				</div>
			),
		},
		{
			title: 'CC',
			dataIndex: 'cc',
			key: 'cc',
			render: (cc: string | null) => (
				<div style={{ maxWidth: '150px' }}>
					{cc ? (
						<span title={cc}>
							{cc.length > 20 ? `${cc.substring(0, 20)}...` : cc}
						</span>
					) : (
						'-'
					)}
				</div>
			),
		},
		{
			title: 'Subjek',
			dataIndex: 'subject',
			key: 'subject',
			render: (subject: string | null) => (
				<div style={{ maxWidth: '250px' }}>
					{subject ? <span title={subject}>{subject}</span> : '-'}
				</div>
			),
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			render: (status: string | null) => (
				<Tag color={getStatusColor(status)}>{status || 'Unknown'}</Tag>
			),
		},
		{
			title: 'Tanggal Kirim',
			dataIndex: 'sendDate',
			key: 'sendDate',
			render: (sendDate: string) => formatDate(sendDate),
		},
	];

	return (
		<div style={{ width: '100%' }}>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginBottom: '24px',
				}}
			>
				<div style={{ width: '100%' }}>
					<FleetTitle level={1}>Email Logs</FleetTitle>
				</div>
			</div>

			<Card>
				<Row
					gutter={[16, 16]}
					style={{ marginBottom: '16px' }}
				>
					<Col
						xs={24}
						sm={12}
						md={6}
					>
						<Search
							placeholder="Cari subjek..."
							onSearch={(value) => handleSearch(value, 'subject')}
							allowClear
							style={{ width: '100%' }}
						/>
					</Col>
					<Col
						xs={24}
						sm={12}
						md={6}
					>
						<Search
							placeholder="Cari penerima..."
							onSearch={(value) => handleSearch(value, 'tos')}
							allowClear
							style={{ width: '100%' }}
						/>
					</Col>
					<Col
						xs={24}
						sm={12}
						md={6}
					>
						<Select
							placeholder="Filter status"
							onChange={(value) => handleSearch(value, 'status')}
							allowClear
							style={{ width: '100%' }}
						>
							<Option value="sent">Sent</Option>
							<Option value="failed">Failed</Option>
							<Option value="pending">Pending</Option>
						</Select>
					</Col>
					<Col
						xs={24}
						sm={12}
						md={6}
					>
						<Button
							icon={<SearchOutlined />}
							onClick={() => {
								setSearchParams({
									pagination: {
										pageSize: 10,
										pageNumber: 1,
									},
								});
							}}
							style={{ width: '100%' }}
						>
							Reset Filter
						</Button>
					</Col>
				</Row>

				<Table
					columns={columns}
					dataSource={filteredLogs}
					loading={isLoading}
					rowKey="id"
					pagination={{
						total: totalCount,
						current: searchParams.pagination?.pageNumber,
						pageSize: searchParams.pagination?.pageSize,
						onChange: handleTableChange,
						showSizeChanger: false,
					}}
				/>
			</Card>
		</div>
	);
};

export default AdminEmailLogsPage;
